import { Operator } from '@dx-shared/rule-engine';
import type { Conditions, Condition } from '@dx-shared/rule-engine';

const languageFactKeys = ['locale', 'language'];
const defaultLanguage = 'en';

/**
 * @temporary
 *
 * This function transforms rule engine conditions to append "en" to the list of languages
 * within "rule-ui-translate-*" feature config conditions.
 *
 * This is used to determine if the language selector should display on specific pages
 * along with including specific languages only within the specific condition.
 *
 * Once all supported pages support all languages, this can be removed.
 */
export function transformRuleEngineLanguages(conditions: Conditions): Conditions {
  if (isAny(conditions)) {
    return {
      ...conditions,
      any: conditions.any.map(transformRuleEngineLanguages),
    };
  }

  if (isAll(conditions)) {
    return {
      ...conditions,
      all: conditions.all.map(transformRuleEngineLanguages),
    };
  }

  if (isNot(conditions)) {
    return conditions;
  }

  // If the fact is "locale" or "language" add "en".
  if (conditions.fact && languageFactKeys.includes(conditions.fact)) {
    const transformedValue = transformValue(conditions.value);
    return {
      fact: 'language',
      operator: Array.isArray(transformedValue) ? Operator.in : conditions.operator,
      value: transformedValue,
    };
  }

  return conditions;
}

function isAny(conditions: Conditions): conditions is { any: Conditions[] } {
  return typeof conditions === 'object' && 'any' in conditions;
}

function isAll(conditions: Conditions): conditions is { all: Conditions[] } {
  return typeof conditions === 'object' && 'all' in conditions;
}

function isNot(conditions: Conditions): conditions is { not: Conditions } {
  return typeof conditions === 'object' && 'not' in conditions;
}

function transformValue(value: Condition['value']) {
  if (Array.isArray(value)) {
    return Array.from(new Set([...value, defaultLanguage]));
  }
  if (typeof value === 'string') {
    return [value, defaultLanguage];
  }
  return value;
}
